<template>
  <SectionPageShell section="dashboard" :title="greetingTitle" :hide-header="user.needsSurvey()">
    <div class="dashboard-page">
    <section class="dashboard-hero surface-panel">
      <div class="row g-4 align-items-start">
        <div class="col-12">
          <div class="d-flex flex-column flex-lg-row align-items-lg-start justify-content-between gap-3 mb-3">
            <p class="dashboard-lead mb-0">
              {{ greetingText }}
            </p>
            <div class="dashboard-status-badge">
              <span class="dashboard-status-badge__state">
                <Icon :icon="attentionState.icon" />
                {{ attentionState.label }}
              </span>
              <span class="dashboard-status-badge__meta">
                <Icon icon="fa-pro-solid:building" />
                {{ organisationLabel }}
              </span>
              <span class="dashboard-status-badge__meta">
                <Icon icon="fa-pro-solid:boxes-stacked" />
                {{ activePackageLabel }}
              </span>
            </div>
          </div>

          <div v-if="!user.needsSurvey()" class="dashboard-hero__next-steps">
            <router-link
              v-if="latestObjectQuickLink"
              :to="quickLinkTarget(latestObjectQuickLink)"
              class="dashboard-next-card"
              :class="getContinueEditThemeClass(latestObjectQuickLink)"
              :style="getContinueEditThemeStyle(latestObjectQuickLink)"
            >
              <span class="dashboard-next-card__main">
                <span class="dashboard-next-card__icon">
                  <Icon :icon="latestObjectQuickLink.icon" />
                </span>
                <span class="dashboard-next-card__body">
                  <span class="dashboard-next-card__eyebrow">Mit der Bearbeitung fortfahren</span>
                  <span class="dashboard-next-card__title">{{ latestObjectQuickLink.name }}</span>
                  <span class="dashboard-next-card__text">{{ latestObjectQuickLink.typeLabel }} · Zuletzt bearbeitet</span>
                </span>
                <Icon icon="fa-pro-solid:chevron-right" class="dashboard-next-card__arrow" />
              </span>
            </router-link>

            <router-link
              v-if="primaryAction"
              :to="{name: primaryAction.route}"
              class="dashboard-next-card"
              :class="{
                'dashboard-next-card--highlight': primaryAction.route === 'packages.index',
                'dashboard-next-card--primary': primaryAction.route !== 'packages.index',
              }"
            >
              <span
                v-if="primaryAction.route === 'packages.index'"
                class="dashboard-next-card__stars"
                aria-hidden="true"
              />
              <span class="dashboard-next-card__main">
                <span
                  class="dashboard-next-card__icon"
                  :class="{'dashboard-next-card__icon--primary': primaryAction.route !== 'packages.index'}"
                >
                  <Icon :icon="primaryAction.icon" />
                </span>
                <span class="dashboard-next-card__body">
                  <span class="dashboard-next-card__eyebrow">Pakete &amp; Leistungen</span>
                  <span class="dashboard-next-card__title">{{ primaryAction.label }}</span>
                  <span class="dashboard-next-card__text">Aktive Vermarktung im Überblick</span>
                </span>
                <Icon icon="fa-pro-solid:chevron-right" class="dashboard-next-card__arrow" />
              </span>
            </router-link>
          </div>

          <div v-if="user.needsSurvey()" class="dashboard-survey-alert mt-3" role="status">
            <div class="dashboard-survey-alert__icon">
              <Icon icon="fa-pro-solid:clipboard-list-check" />
            </div>
            <div class="dashboard-survey-alert__body">
              <strong>Wichtig: Bitte zuerst den Fragebogen abschließen</strong>
              <p class="mb-0">
                Erst danach können wir Ihre Vermarktungsbereiche korrekt freischalten und die weiteren Schritte zuverlässig vorbereiten.
              </p>
            </div>
            <router-link :to="{name: 'account.survey'}" class="btn btn-warning dashboard-survey-alert__cta">
              Jetzt starten
            </router-link>
          </div>
        </div>

      </div>
    </section>

    <template v-if="!user.needsSurvey()">
    <article class="surface-panel dashboard-panel">
        <div class="dashboard-panel__header">
          <div>
            <div class="dashboard-panel__eyebrow">Schnellzugriff</div>
            <h2 class="dashboard-panel__title">Ihre wichtigsten Bereiche</h2>
          </div>
        </div>

        <div class="dashboard-shortcuts">
          <div
            v-for="item in quickLinks"
            :key="item.route"
            class="dashboard-shortcut-wrap"
          >
            <router-link
              :to="quickLinkTarget(item)"
              custom
              v-slot="{ href, navigate }"
            >
              <component
                :is="item.disabled ? 'div' : 'a'"
                :href="item.disabled ? undefined : href"
                class="dashboard-shortcut"
                :class="[
                  {'dashboard-shortcut--highlight': item.highlight},
                  {'dashboard-shortcut--disabled': item.disabled},
                  getShortcutThemeClass(item),
                ]"
                :style="getShortcutThemeStyle(item)"
                :aria-disabled="item.disabled ? 'true' : undefined"
                @click="handleShortcutClick($event, navigate, item)"
              >
                <span class="dashboard-shortcut__icon">
                  <Icon :icon="item.icon" />
                </span>
                <span class="dashboard-shortcut__body">
                  <span class="dashboard-shortcut__title">{{ item.name }}</span>
                  <span class="dashboard-shortcut__text">{{ item.description }}</span>
                </span>
                <Icon icon="fa-pro-solid:chevron-right" class="dashboard-shortcut__arrow" />
              </component>
            </router-link>
            <button
              v-if="showDisabledHint(item)"
              type="button"
              class="dashboard-shortcut-hint-btn"
              title="Passendes Paket ansehen"
              @click.stop.prevent="goToDisabledHint(item)"
            >
              <span
                class="dashboard-shortcut-hint-rocket"
                :class="{'dashboard-shortcut-hint-rocket--launch': isHintLaunching(item)}"
              >
                <Icon icon="streamline-ultimate:startup-product-rocket-box-bold" />
              </span>
            </button>
          </div>
        </div>
      </article>
    </template>
    </div>
  </SectionPageShell>
</template>

<script>
import SectionPageShell from "@/components/base_type/SectionPageShell.vue";
import {useUserStore} from "@/stores/user";
import {getBaseTypeTheme, getBaseTypeThemeClass, getBaseTypeThemeStyle, resolveBaseTypeKey} from "@/utils/baseTypeTheme";

const QUICK_LINK_META = {
  'accommodations.index': {
    description: 'Unterkünfte und Inhalte pflegen'
  },
  'offers.index': {
    description: 'Angebote verwalten und aktualisieren'
  },
  'events.index': {
    description: 'Veranstaltungen planen und veröffentlichen'
  },
  'discover.index': {
    description: 'Entdecken-Einträge pflegen und veröffentlichen'
  },
  'ads.index': {
    description: 'Kampagnen und Anzeigen organisieren'
  },
  'websites.index': {
    description: 'Webseiten und Ausspielungen überblicken'
  },
  'domains.index': {
    description: 'Domains und Registrierungsdaten einsehen'
  },
  'media.index': {
    description: 'Dateien, Bilder und Dokumente sammeln'
  },
  'account.settings': {
    description: 'Kontodaten, Rechnungs- und Profildaten'
  },
  'account.support': {
    description: 'Fragen klären und Hilfe anfordern'
  },
  'account.survey': {
    description: 'Grunddaten für den Vermarktungsstart erfassen'
  },
}

const OBJECT_EDIT_ROUTES = {
  unterkunft: 'accommodations.edit',
  angebote: 'offers.edit',
  termine: 'events.edit',
  entdecken: 'discover.edit',
}

export default {
  name: "Dashboard",
  components: {
    SectionPageShell,
  },
  setup() {
    const userStore = useUserStore()
    return {userStore}
  },
  data() {
    return {
      launchingHintKey: null,
    }
  },
  computed: {
    user() {
      return this.userStore.user;
    },
    organisation() {
      return this.user.organisation || {}
    },
    organisationLabel() {
      return this.organisation?.name || 'Noch keine Organisation ausgewählt'
    },
    activePackages() {
      return this.organisation?.active_packages || []
    },
    activePackageCount() {
      return this.activePackages.length
    },
    activePackageLabel() {
      return this.activePackageCount === 1
          ? '1 aktives Paket'
          : `${this.activePackageCount} aktive Pakete`
    },
    greetingTitle() {
      return `Willkommen zurück, ${this.user.first_name || this.user.email || 'im Kundenbereich'}`
    },
    greetingText() {
      if (this.user.needsSurvey()) {
        return 'Starten Sie mit dem Fragebogen, damit wir Ihre Vermarktung korrekt aufsetzen und die ersten Bereiche für Sie vorbereiten können.'
      }

      if (!this.user.currentOrganisationPresent()) {
        return 'Wählen Sie eine Organisation aus oder hinterlegen Sie Ihre Daten, damit wir Ihre Vermarktung klar zuordnen können.'
      }

      return 'Behalten Sie Ihre Vermarktung, Inhalte und Pakete an einem Ort im Blick. Die wichtigsten Bereiche und nächsten Schritte finden Sie direkt hier.'
    },
    attentionState() {
      if (this.user.needsSurvey()) {
        return {
          icon: 'fa-pro-solid:clipboard-list-check',
          label: 'Einrichtung ausstehend',
        }
      }

      if (this.user.needsAttention()) {
        return {
          icon: 'fa-pro-solid:triangle-exclamation',
          label: 'Offene Punkte',
        }
      }

      return {
        icon: 'fa-pro-solid:circle-check',
        label: 'Bereit für die Vermarktung',
      }
    },
    primaryAction() {
      if (this.user.needsSurvey()) {
        return {
          route: 'account.survey',
          label: 'Fragebogen starten',
          icon: 'fa-pro-solid:clipboard-list-check',
        }
      }

      if (!this.user.currentOrganisationPresent()) {
        return {
          route: 'organisations.index',
          label: 'Organisation wählen',
          icon: 'fa-pro-solid:building',
        }
      }

      return {
        route: 'packages.index',
        label: 'Pakete und Leistungen ansehen',
        icon: 'streamline-ultimate:startup-product-rocket-box-bold',
      }
    },
    latestObjectQuickLink() {
      const account = this.user.getOrganisations().find(
          (candidate) => String(candidate.kdnr) === String(this.organisation?.kdnr)
      )
      const object = this.organisation?.latest_object || account?.latest_object
      const route = OBJECT_EDIT_ROUTES[object?.base_type]

      if (!object?.id || !route) {
        return null
      }

      const theme = getBaseTypeTheme(object.base_type)

      return {
        name: object.name || 'Objekt',
        route,
        routeParams: {id: object.id, lang: 'de'},
        icon: theme.icon,
        baseType: object.base_type,
        typeLabel: theme.shortLabel,
      }
    },
    quickLinks() {
      const groups = this.user.getCustomerMenuItems()
      const menuLinks = Object.values(groups)
          .flatMap((group) => Object.values(group.children || {}).map((item) => ({
            ...item,
            disabled: Boolean(item.disabled || group.disabled),
          })))
          .filter((item) => !item.hide && !['dashboard', 'packages.index'].includes(item.route))
          .map((item) => ({
            ...item,
            description: QUICK_LINK_META[item.route]?.description || 'Bereich öffnen',
          }))

      return menuLinks.slice(0, 8)
    },
  },
  methods: {
    quickLinkTarget(item) {
      return {name: item.route, params: item.routeParams || {}}
    },
    handleShortcutClick(event, navigate, item) {
      if (item.disabled) {
        event.preventDefault()
        return
      }

      navigate(event)
    },
    showDisabledHint(item) {
      return item?.disabled && item?.disabledHintRoute
    },
    goToDisabledHint(item) {
      if (!item?.disabledHintRoute) {
        return
      }

      const hintKey = this.getHintKey(item)
      this.launchingHintKey = hintKey

      window.setTimeout(() => {
        if (this.launchingHintKey === hintKey) {
          this.launchingHintKey = null
        }
      }, 650)

      window.setTimeout(() => {
        this.$router.push({name: item.disabledHintRoute})
      }, 220)
    },
    getHintKey(item) {
      return `${item?.route || 'unknown'}:${item?.disabledHintRoute || 'unknown'}`
    },
    isHintLaunching(item) {
      return this.launchingHintKey === this.getHintKey(item)
    },
    getMarketingBaseType(value) {
      if (typeof value !== 'string') {
        return null
      }

      const routePrefix = value.split('.')[0]
      if (!['accommodations', 'offers', 'events', 'discover', 'unterkunft', 'angebote', 'termine', 'entdecken'].includes(routePrefix) && !['unterkunft', 'angebote', 'termine', 'entdecken'].includes(value)) {
        return null
      }

      return resolveBaseTypeKey(value)
    },
    getContinueEditThemeClass(item) {
      return item?.baseType ? getBaseTypeThemeClass(item.baseType) : ''
    },
    getContinueEditThemeStyle(item) {
      return item?.baseType ? getBaseTypeThemeStyle(item.baseType) : {}
    },
    getShortcutThemeClass(item) {
      const baseType = this.getMarketingBaseType(item?.route)
      return baseType ? getBaseTypeThemeClass(baseType) : ''
    },
    getShortcutThemeStyle(item) {
      const baseType = this.getMarketingBaseType(item?.route)
      return baseType ? getBaseTypeThemeStyle(baseType) : {}
    },
  },
}
</script>

<style scoped lang="scss">
.dashboard-page {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-bottom: 1rem;
}

.dashboard-hero,
.dashboard-panel {
  padding: 1.5rem;
}

.dashboard-hero {
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(circle at top right, rgba(48, 185, 236, 0.18), transparent 24rem),
    linear-gradient(135deg, rgba(255, 255, 255, 0.92), rgba(242, 248, 252, 0.9));
}

.dashboard-eyebrow,
.dashboard-panel__eyebrow {
  font-size: 0.73rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #5f7283;
}

.dashboard-title {
  margin: 0.35rem 0 0.55rem;
  font-size: clamp(2rem, 4vw, 3.2rem);
  line-height: 0.98;
  color: #0d2f47;
}

.dashboard-lead {
  max-width: 46rem;
  margin: 0;
  color: #50697d;
  font-size: 1.04rem;
}

.dashboard-status-badge {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.55rem 0;
  padding: 0.25rem 0;
  color: #49677c;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.dashboard-status-badge__state,
.dashboard-status-badge__meta {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  white-space: nowrap;
}

.dashboard-status-badge__state svg {
  padding: 0.3rem;
  width: 1.55rem;
  height: 1.55rem;
  border-radius: 50%;
  background: rgba(13, 79, 113, 0.09);
  color: #0d4f71;
}

.dashboard-status-badge__meta {
  margin-left: 0.75rem;
  padding-left: 0.75rem;
  border-left: 1px solid rgba(13, 62, 92, 0.16);
}

.dashboard-status-badge__meta svg {
  color: #6b8495;
  font-size: 0.78rem;
}

.dashboard-hero__next-steps {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.9rem;
  margin-top: 1.4rem;
}

.dashboard-hero__next-steps:has(.dashboard-next-card:only-child) {
  grid-template-columns: minmax(0, 28rem);
}

.dashboard-next-card {
  display: block;
  position: relative;
  padding: 1rem 1.05rem;
  border: 1px solid rgba(10, 57, 85, 0.08);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(244, 248, 251, 0.94));
  color: #16324a;
  text-decoration: none;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.dashboard-next-card__main {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 0.9rem;
  align-items: center;
  position: relative;
  z-index: 1;
}

.dashboard-next-card:hover,
.dashboard-next-card:focus-visible {
  transform: translateY(-2px);
  box-shadow: 0 18px 32px rgba(10, 33, 48, 0.08);
  border-color: rgba(27, 108, 151, 0.18);
}

.dashboard-next-card--primary {
  background:
    radial-gradient(circle at top right, rgba(48, 185, 236, 0.22), transparent 42%),
    linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(232, 244, 252, 0.94));
  border-color: rgba(27, 108, 151, 0.14);
}

.dashboard-next-card--highlight {
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(circle at top right, rgba(126, 214, 246, 0.32), transparent 48%),
    linear-gradient(135deg, #0d4f71 0%, #1b6c97 58%, #2589b8 100%);
  border-color: rgba(126, 214, 246, 0.42);
  box-shadow: 0 12px 28px rgba(13, 79, 113, 0.22);
  color: #fff;
}

.dashboard-next-card--highlight:hover,
.dashboard-next-card--highlight:focus-visible {
  border-color: rgba(126, 214, 246, 0.58);
  box-shadow: 0 18px 32px rgba(13, 79, 113, 0.28);
}

.dashboard-next-card--highlight .dashboard-next-card__eyebrow,
.dashboard-next-card--highlight .dashboard-next-card__text,
.dashboard-next-card--highlight .dashboard-next-card__arrow {
  color: rgba(221, 239, 249, 0.82);
}

.dashboard-next-card--highlight .dashboard-next-card__title {
  color: #fff;
}

.dashboard-next-card--highlight .dashboard-next-card__icon {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.24), rgba(255, 255, 255, 0.08));
  border: 1px solid rgba(255, 255, 255, 0.22);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.18);
}

.dashboard-next-card__stars {
  position: absolute;
  inset: -85% 0;
  z-index: 0;
  display: block;
  background-repeat: repeat-y;
  background-size: 100% 88px;
  background-position: 0 0;
  opacity: 0.95;
  pointer-events: none;
  background-image:
    radial-gradient(circle at 12% 10%, rgba(255, 255, 255, 0.98) 0 2px, transparent 3.2px),
    radial-gradient(circle at 38% 28%, rgba(198, 235, 255, 0.95) 0 2.2px, transparent 3.4px),
    radial-gradient(circle at 66% 18%, rgba(255, 255, 255, 0.92) 0 1.8px, transparent 3px),
    radial-gradient(circle at 84% 34%, rgba(180, 228, 255, 0.95) 0 2px, transparent 3.2px);
  animation: dashboard-star-rain 3.8s linear infinite;
}

.dashboard-next-card__stars::before,
.dashboard-next-card__stars::after {
  position: absolute;
  inset: -85% 0;
  display: block;
  background-repeat: repeat-y;
  background-size: 100% 88px;
  background-position: 0 0;
  opacity: 0.95;
  pointer-events: none;
  content: "";
}

.dashboard-next-card__stars::before {
  background-image:
    radial-gradient(circle at 20% 16%, rgba(255, 255, 255, 0.78) 0 1.5px, transparent 2.6px),
    radial-gradient(circle at 52% 24%, rgba(198, 235, 255, 0.82) 0 1.7px, transparent 2.8px),
    radial-gradient(circle at 76% 12%, rgba(255, 255, 255, 0.74) 0 1.5px, transparent 2.6px);
  animation: dashboard-star-rain 5.4s linear infinite reverse;
}

.dashboard-next-card__stars::after {
  background-image:
    radial-gradient(circle at 8% 30%, rgba(255, 255, 255, 0.72) 0 1.3px, transparent 2.3px),
    radial-gradient(circle at 44% 8%, rgba(220, 243, 255, 0.78) 0 1.4px, transparent 2.4px),
    radial-gradient(circle at 72% 26%, rgba(255, 255, 255, 0.68) 0 1.3px, transparent 2.3px),
    radial-gradient(circle at 92% 14%, rgba(180, 228, 255, 0.72) 0 1.4px, transparent 2.4px);
  animation: dashboard-star-rain 6.8s linear infinite;
}

.dashboard-next-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  background: #0f4768;
  color: #fff;
  font-size: 1.15rem;
  flex-shrink: 0;
}

.dashboard-next-card__icon--primary {
  background: linear-gradient(135deg, #0d4f71, #30b9ec);
}

.dashboard-next-card__body {
  min-width: 0;
}

.dashboard-next-card__eyebrow {
  display: block;
  font-size: 0.73rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #5f7283;
}

.dashboard-next-card__title {
  display: block;
  margin-top: 0.18rem;
  font-weight: 700;
  color: #123754;
  line-height: 1.25;
}

.dashboard-next-card--primary .dashboard-next-card__title {
  color: #0d4f71;
}

.dashboard-next-card__text {
  display: block;
  margin-top: 0.22rem;
  color: #5f7283;
  font-size: 0.9rem;
}

.dashboard-next-card__arrow {
  color: #5f7283;
  flex-shrink: 0;
}

.dashboard-next-card.base-type-accommodations,
.dashboard-next-card.base-type-offers,
.dashboard-next-card.base-type-events,
.dashboard-next-card.base-type-discover {
  border-color: var(--base-type-border);
  background: linear-gradient(180deg, #ffffff, var(--base-type-surface));
}

.dashboard-next-card.base-type-accommodations .dashboard-next-card__icon,
.dashboard-next-card.base-type-offers .dashboard-next-card__icon,
.dashboard-next-card.base-type-events .dashboard-next-card__icon,
.dashboard-next-card.base-type-discover .dashboard-next-card__icon {
  background: var(--base-type-primary);
}

.dashboard-next-card.base-type-accommodations .dashboard-next-card__title,
.dashboard-next-card.base-type-offers .dashboard-next-card__title,
.dashboard-next-card.base-type-events .dashboard-next-card__title,
.dashboard-next-card.base-type-discover .dashboard-next-card__title {
  color: var(--base-type-text);
}

.dashboard-survey-alert {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  padding: 0.95rem 1rem;
  border: 1px solid #f1c37f;
  border-left: 0.35rem solid #c86b1d;
  background:
    radial-gradient(circle at top right, rgba(240, 166, 47, 0.18), transparent 46%),
    #fff9ef;
  color: #6d3e10;
}

.dashboard-survey-alert__icon {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.1rem;
  height: 2.1rem;
  background: rgba(200, 107, 29, 0.12);
  color: #b55612;
  font-size: 1rem;
}

.dashboard-survey-alert__body {
  flex: 1;
}

.dashboard-survey-alert__body strong {
  display: block;
  margin-bottom: 0.1rem;
  color: #5f3207;
}

.dashboard-survey-alert__cta {
  flex-shrink: 0;
  font-weight: 700;
}

.dashboard-panel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.dashboard-panel__title {
  margin: 0.3rem 0 0;
  font-size: 1.4rem;
  color: #123754;
}

.dashboard-shortcuts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.9rem;
}

.dashboard-shortcut-wrap {
  position: relative;
}

.dashboard-shortcut {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 0.9rem;
  align-items: center;
  padding: 1rem;
  border: 1px solid rgba(10, 57, 85, 0.08);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(244, 248, 251, 0.94));
  color: #16324a;
  text-decoration: none;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.dashboard-shortcut:hover,
.dashboard-shortcut:focus-visible {
  transform: translateY(-2px);
  box-shadow: 0 18px 32px rgba(10, 33, 48, 0.08);
  border-color: rgba(27, 108, 151, 0.18);
}

.dashboard-shortcut--disabled,
.dashboard-shortcut--disabled:hover,
.dashboard-shortcut--disabled:focus-visible {
  opacity: 0.25;
  cursor: default;
  transform: none;
  box-shadow: none;
}

.dashboard-shortcut-hint-btn {
  position: absolute;
  top: 50%;
  right: 2.75rem;
  transform: translateY(-50%);
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: #f1b24a;
  cursor: pointer;
}

.dashboard-shortcut-hint-rocket {
  display: inline-flex;
  transform-origin: 50% 50%;
}

.dashboard-shortcut-hint-rocket--launch {
  animation: dashboard-rocket-launch 0.65s cubic-bezier(0.22, 1, 0.36, 1);
}

.dashboard-shortcut-hint-btn:hover,
.dashboard-shortcut-hint-btn:focus-visible {
  color: #ffd37a;
}

@keyframes dashboard-star-rain {
  from {
    background-position: 0 0;
  }

  to {
    background-position: 0 88px;
  }
}

@keyframes dashboard-rocket-launch {
  0% {
    transform: translate3d(0, 0, 0) scale(1);
    opacity: 1;
  }

  35% {
    transform: translate3d(0, -140px, 0) scale(1.08);
    opacity: 0;
  }

  55% {
    transform: translate3d(0, 14px, 0) scale(0.96);
    opacity: 0;
  }

  80% {
    transform: translate3d(0, 0, 0) scale(1);
    opacity: 0.55;
  }

  100% {
    transform: translate3d(0, 0, 0) scale(1);
    opacity: 1;
  }
}

.dashboard-shortcut--highlight {
  background:
    radial-gradient(circle at top right, rgba(240, 166, 47, 0.18), transparent 36%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(249, 246, 240, 0.96));
}

.dashboard-shortcut__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  background: #0f4768;
  color: #fff;
  font-size: 1.15rem;
}

.dashboard-shortcut__body {
  min-width: 0;
}

.dashboard-shortcut__title {
  display: block;
  font-weight: 700;
  color: #123754;
}

.dashboard-shortcut__text {
  display: block;
  margin-top: 0.22rem;
  color: #5f7283;
  font-size: 0.9rem;
}

.dashboard-shortcut__arrow {
  color: #5f7283;
}

.dashboard-shortcut.base-type-accommodations,
.dashboard-shortcut.base-type-offers,
.dashboard-shortcut.base-type-events,
.dashboard-shortcut.base-type-discover {
  border-color: var(--base-type-border);
  background: linear-gradient(180deg, #ffffff, var(--base-type-surface));
}

.dashboard-shortcut.base-type-accommodations .dashboard-shortcut__icon,
.dashboard-shortcut.base-type-offers .dashboard-shortcut__icon,
.dashboard-shortcut.base-type-events .dashboard-shortcut__icon,
.dashboard-shortcut.base-type-discover .dashboard-shortcut__icon {
  background: var(--base-type-primary);
}

.dashboard-shortcut.base-type-accommodations .dashboard-shortcut__title,
.dashboard-shortcut.base-type-offers .dashboard-shortcut__title,
.dashboard-shortcut.base-type-events .dashboard-shortcut__title,
.dashboard-shortcut.base-type-discover .dashboard-shortcut__title {
  color: var(--base-type-text);
}

@media (max-width: 767.98px) {
  .dashboard-hero,
  .dashboard-panel {
    padding: 1.15rem;
  }

  .dashboard-status-badge {
    flex-direction: column;
    align-items: flex-start;
  }

  .dashboard-status-badge__meta {
    margin-left: 0;
    padding-left: 0;
    border-left: 0;
  }

  .dashboard-hero__next-steps {
    grid-template-columns: 1fr;
  }

  .dashboard-shortcuts {
    grid-template-columns: 1fr;
  }

}
</style>
